import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Check, ChevronDown, ChevronUp,
  User, Sparkles, RefreshCw, Dumbbell, Utensils, 
  TrendingUp, BarChart3, Quote, Globe, Calendar,
  Shield, Clock, Target, Zap, Users, CheckCircle2,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO, { pageSEO } from '../components/SEO';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

export default function Landing() {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Four Pillars of Differentiation
  const pillars = [
    { 
      icon: User, 
      title: 'Truly Personalised Programs', 
      desc: 'Built from your body data, goals, training history & lifestyle — not copy-paste workouts.' 
    },
    { 
      icon: Utensils, 
      title: 'Culturally Adaptive Meal Plans', 
      desc: 'Meals designed around your culture, preferences, allergies & intolerances.' 
    },
    { 
      icon: RefreshCw, 
      title: 'Weekly Program Updates', 
      desc: 'Your plan evolves with your progress — just like working with a real coach.' 
    },
    { 
      icon: TrendingUp, 
      title: 'Track. Adjust. Improve.', 
      desc: 'Smart tracking that refines your plan in real time.' 
    },
  ];

  // How It Works Steps
  const steps = [
    { 
      num: '01', 
      title: 'Tell Us About You', 
      desc: 'Share your goals, body metrics, diet preferences & lifestyle in a quick quiz.' 
    },
    { 
      num: '02', 
      title: 'Get Your Personal Plan', 
      desc: 'We generate your personalised fitness & nutrition plan instantly.' 
    },
    { 
      num: '03', 
      title: 'Follow, Track & Improve', 
      desc: 'Follow your plan, track progress and receive weekly updates as you evolve.' 
    },
  ];

  // Approved Testimonials
  const testimonials = [
    { 
      quote: "This is the first plan that actually feels like it was built for me.", 
      name: "James", 
      location: "Australia",
      initials: "JA"
    },
    { 
      quote: "Finally a meal plan that respects my culture and food choices.", 
      name: "Aisha", 
      location: "USA",
      initials: "AU"
    },
    { 
      quote: "The weekly updates make it feel like I have a real coach.", 
      name: "Marcus", 
      location: "Singapore",
      initials: "MS"
    },
  ];

  // Trust Stats
  const trustStats = [
    { value: '1,000+', label: 'Plans Generated' },
    { value: '10+', label: 'Countries' },
    { value: '14-21', label: 'Days to Results' },
  ];

  // FAQs
  const faqs = [
    { 
      q: "Is this suitable for beginners?", 
      a: "Absolutely. Your plan is built based on your current fitness level, whether you're just starting out or an experienced athlete." 
    },
    { 
      q: "Can I train at home without equipment?", 
      a: "Yes. Your plan adapts to your available equipment — home, gym, outdoors, or bodyweight only." 
    },
    { 
      q: "What about food allergies and cultural preferences?", 
      a: "Your meal plan is fully customised to respect your culture, allergies, intolerances and food preferences." 
    },
    { 
      q: "How does the weekly update work?", 
      a: "Each week, your program refreshes based on your logged progress, feedback and goals — just like having a real coach." 
    },
    { 
      q: "Can I cancel anytime?", 
      a: "Yes. No lock-in contracts. Cancel anytime during your trial or subscription." 
    },
  ];

  // Pricing Plans (keeping existing structure)
  const plans = [
    { 
      id: 'monthly', 
      name: 'Monthly', 
      price: 10.00, 
      period: 'month',
      perMonth: 10,
    },
    { 
      id: 'quarterly', 
      name: '3 Months', 
      price: 27.00, 
      period: '3 months',
      perMonth: 9,
      discount: '10%',
      popular: true,
    },
    { 
      id: 'yearly', 
      name: 'Yearly', 
      price: 96.00, 
      period: 'year',
      perMonth: 8,
      discount: '20%',
      bestValue: true,
    },
  ];

  return (
    <>
      <SEO {...pageSEO.landing} />
      <div className="min-h-screen bg-black text-white overflow-x-hidden">

        {/* Header - Always Sticky */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-zinc-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-[#CCFF00] flex items-center justify-center">
                <span className="text-black font-black text-lg">V</span>
              </div>
              <span className="font-bold text-xl">VYTA</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('how-it-works')} className="text-sm text-zinc-400 hover:text-white transition">How It Works</button>
              <button onClick={() => scrollToSection('why-vyta')} className="text-sm text-zinc-400 hover:text-white transition">Why Vyta</button>
              <button onClick={() => scrollToSection('pricing')} className="text-sm text-zinc-400 hover:text-white transition">Pricing</button>
              <Link to="/challenge" className="text-sm text-zinc-400 hover:text-white transition">Challenge</Link>
            </nav>
            <div className="flex items-center gap-3">
              <Link to="/login" className="text-sm text-zinc-400 hover:text-white transition hidden sm:block">Log In</Link>
              <Button
                onClick={() => navigate('/register')}
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-5 py-2"
                data-testid="header-cta-btn"
              >
                Get Started
              </Button>
            </div>
          </div>
        </header>

        {/* HERO SECTION */}
        <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/50 via-black to-black" />
          <div className="absolute top-1/4 left-1/4 w-[300px] sm:w-[400px] md:w-[600px] h-[300px] sm:h-[400px] md:h-[600px] rounded-full bg-[#CCFF00] opacity-[0.03] blur-[120px]" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-32">
            <motion.div 
              initial="hidden" 
              animate="visible" 
              variants={stagger}
              className="max-w-4xl mx-auto text-center"
            >
              {/* Badge */}
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/50 border border-zinc-700/50 mb-8">
                <Sparkles className="w-4 h-4 text-[#CCFF00]" />
                <span className="text-sm text-zinc-300">Your Digital Personal Coach + Nutritionist</span>
              </motion.div>
              
              {/* Headline */}
              <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6">
                Get a fitness & meal plan built around{' '}
                <span className="text-[#CCFF00]">YOUR</span> body, culture & goals
                <span className="block text-zinc-400 text-2xl sm:text-3xl lg:text-4xl font-bold mt-4">
                  — updated weekly
                </span>
              </motion.h1>
              
              {/* Subheadline */}
              <motion.p variants={fadeUp} className="text-lg sm:text-xl text-zinc-300 max-w-2xl mx-auto mb-10">
                Lose fat, build muscle, or train for events with programs that adapt as your body changes — not generic templates.
              </motion.p>
              
              {/* CTAs */}
              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-8 py-6 text-lg"
                  data-testid="hero-cta-btn"
                >
                  Build My Personalised Plan
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  variant="outline"
                  className="border-zinc-700 text-white hover:bg-zinc-800 rounded-full px-8 py-6 text-lg"
                  data-testid="hero-trial-btn"
                >
                  Start 7-Day Free Trial
                </Button>
              </motion.div>
              
              {/* Trust indicators */}
              <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-white/80">7-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-white/80">Cancel anytime</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-white/80">No generic templates</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
          
          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <motion.div 
              animate={{ y: [0, 8, 0] }} 
              transition={{ repeat: Infinity, duration: 2 }}
              className="w-6 h-10 rounded-full border-2 border-zinc-700 flex items-start justify-center p-2"
            >
              <div className="w-1.5 h-2.5 rounded-full bg-zinc-500" />
            </motion.div>
          </div>
        </section>

        {/* WHY VYTA WORKS - DIFFERENTIATION SECTION */}
        <section id="why-vyta" className="py-24 bg-zinc-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4">
                Why Vyta Works <span className="text-[#CCFF00]">When Others Don't</span>
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto">
                We're not another fitness app. We're your digital personal coach + nutritionist.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {pillars.map((pillar, i) => (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-[#CCFF00]/30 transition-colors group"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#CCFF00]/10 flex items-center justify-center mb-5 group-hover:bg-[#CCFF00]/20 transition-colors">
                    <pillar.icon className="w-7 h-7 text-[#CCFF00]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{pillar.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{pillar.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="py-24 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4">
                How It <span className="text-[#CCFF00]">Works</span>
              </h2>
              <p className="text-white/80 text-lg">Three simple steps to your personalised plan</p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {steps.map((step, i) => (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="relative"
                >
                  {/* Connector line */}
                  {i < steps.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#CCFF00]/50 to-transparent" />
                  )}
                  
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#CCFF00]/10 border-2 border-[#CCFF00]/30 mb-6">
                      <span className="text-3xl font-black text-[#CCFF00]">{step.num}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                    <p className="text-white/70">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Button
                onClick={() => navigate('/register')}
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-8 py-6 text-lg"
                data-testid="how-it-works-cta-btn"
              >
                Get My Plan Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* WHAT YOU'LL GET - Plan Screenshots */}
        <section className="py-24 bg-zinc-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4">
                What You'll <span className="text-[#CCFF00]">Get</span>
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto">
                Personalised training and nutrition plans built around your body, goals and food preferences
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {/* Training Plan - Real Screenshot */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0 }}
                className="text-center"
              >
                <div className="relative rounded-2xl overflow-hidden border border-zinc-800 mb-5 bg-zinc-900 shadow-xl shadow-black/20">
                  <img 
                    src="/images/training-screenshot.jpg" 
                    alt="VYTA Training Program"
                    className="w-full h-auto max-h-[450px] object-cover object-top"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4 pt-12">
                    <span className="inline-block bg-[#CCFF00] text-black font-bold text-xs px-3 py-1 rounded-full">REAL APP</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Training Programs</h3>
                <p className="text-white/70 text-base leading-relaxed">Structured weekly workouts with exercises, sets, reps and rest times</p>
              </motion.div>

              {/* Meal Plan Overview - Real Screenshot */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-center"
              >
                <div className="relative rounded-2xl overflow-hidden border border-zinc-800 mb-5 bg-zinc-900 shadow-xl shadow-black/20">
                  <img 
                    src="/images/nutrition-screenshot.jpg" 
                    alt="VYTA Nutrition Plan"
                    className="w-full h-auto max-h-[450px] object-cover object-top"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4 pt-12">
                    <span className="inline-block bg-[#CCFF00] text-black font-bold text-xs px-3 py-1 rounded-full">REAL APP</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Nutrition Plans</h3>
                <p className="text-white/70 text-base leading-relaxed">Daily calorie and macro targets tailored to your goals</p>
              </motion.div>

              {/* Meals Detail - Real Screenshot */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <div className="relative rounded-2xl overflow-hidden border border-zinc-800 mb-5 bg-zinc-900 shadow-xl shadow-black/20">
                  <img 
                    src="/images/meals-screenshot.jpg" 
                    alt="VYTA Meal Details"
                    className="w-full h-auto max-h-[450px] object-cover object-top"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4 pt-12">
                    <span className="inline-block bg-[#CCFF00] text-black font-bold text-xs px-3 py-1 rounded-full">REAL APP</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Detailed Meals</h3>
                <p className="text-white/70 text-base leading-relaxed">Complete recipes with ingredients and cooking instructions</p>
              </motion.div>
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Button
                onClick={() => navigate('/register')}
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-8 py-6 text-lg"
              >
                Start Your 7-Day Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* RISK REVERSAL / FREE TRIAL SECTION */}
        <section className="py-24 bg-gradient-to-b from-zinc-950 to-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center p-8 md:p-12 rounded-3xl bg-gradient-to-br from-[#CCFF00]/10 via-zinc-900/50 to-zinc-900/50 border border-[#CCFF00]/20"
            >
              <div className="w-20 h-20 rounded-full bg-[#CCFF00]/20 flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-[#CCFF00]" />
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-black mb-4">
                Start <span className="text-[#CCFF00]">Risk-Free</span>
              </h2>
              
              <p className="text-xl text-zinc-300 mb-8 max-w-2xl mx-auto">
                7-Day Free Trial — Full Access, No Risk
              </p>
              
              <div className="grid sm:grid-cols-3 gap-4 mb-8 text-left max-w-xl mx-auto">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/50">
                  <CheckCircle2 className="w-5 h-5 text-[#CCFF00] flex-shrink-0" />
                  <span className="text-sm">Full personalised plan</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/50">
                  <CheckCircle2 className="w-5 h-5 text-[#CCFF00] flex-shrink-0" />
                  <span className="text-sm">Weekly updates</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/50">
                  <CheckCircle2 className="w-5 h-5 text-[#CCFF00] flex-shrink-0" />
                  <span className="text-sm">Cancel anytime</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-8 py-6 text-lg"
                  data-testid="trial-cta-btn"
                >
                  Start My Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* TRUST & AUTHORITY */}
        <section className="py-24 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-black mb-4">
                Built by Coaches. <span className="text-[#CCFF00]">Used by Real People.</span>
              </h2>
              <p className="text-zinc-400 text-lg max-w-3xl mx-auto">
                Developed by experienced coaches and performance specialists, Vyta-Fit combines real-world coaching logic with intelligent automation to deliver truly adaptive programs.
              </p>
            </motion.div>
            
            {/* Trust Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 mb-16 max-w-2xl mx-auto">
              {trustStats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center p-4 md:p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800"
                >
                  <div className="text-2xl md:text-4xl font-black text-[#CCFF00] mb-1">{stat.value}</div>
                  <div className="text-xs md:text-sm text-zinc-400">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            
            {/* Trust signals */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#CCFF00]" />
                <span>Used across Australia, USA & Asia</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#CCFF00]" />
                <span>Visible results in 14–21 days</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-[#CCFF00]" />
                <span>Real coaching methodologies</span>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-24 bg-zinc-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-black mb-4">
                What Our <span className="text-[#CCFF00]">Members Say</span>
              </h2>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, i) => (
                <motion.div
                  key={testimonial.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800"
                >
                  <Quote className="w-8 h-8 text-[#CCFF00]/30 mb-4" />
                  <p className="text-lg mb-6 text-zinc-200">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#CCFF00] to-emerald-500 flex items-center justify-center text-black font-bold text-sm">
                      {testimonial.initials}
                    </div>
                    <div>
                      <p className="font-medium">{testimonial.name}</p>
                      <p className="text-sm text-zinc-500">{testimonial.location}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="py-24 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-black mb-4">
                Simple, <span className="text-[#CCFF00]">Affordable</span> Pricing
              </h2>
              <p className="text-zinc-400 text-lg">Start your 7-day free trial. Cancel anytime.</p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
              {plans.map((plan, i) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative p-6 rounded-2xl border ${
                    plan.popular 
                      ? 'bg-gradient-to-b from-[#CCFF00]/10 to-transparent border-[#CCFF00] scale-105' 
                      : plan.bestValue
                      ? 'bg-gradient-to-b from-emerald-500/10 to-transparent border-emerald-500/30'
                      : 'bg-zinc-900/30 border-zinc-800'
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#CCFF00] text-black text-xs font-bold">
                      Most Popular
                    </div>
                  )}
                  {plan.bestValue && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold">
                      Best Value
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold mb-1 mt-2">{plan.name}</h3>
                  
                  {plan.discount && (
                    <div className="inline-block px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold mb-2">
                      Save {plan.discount}
                    </div>
                  )}
                  
                  <div className="mb-1">
                    <span className="text-4xl font-black">${plan.price}</span>
                    <span className="text-zinc-400 text-sm">/{plan.period}</span>
                  </div>
                  <p className="text-sm text-zinc-500 mb-6">${plan.perMonth}/month</p>
                  
                  <ul className="space-y-2 mb-6 text-sm">
                    <li className="flex items-center gap-2 text-zinc-300"><Check className="w-4 h-4 text-[#CCFF00]" />Personalised training</li>
                    <li className="flex items-center gap-2 text-zinc-300"><Check className="w-4 h-4 text-[#CCFF00]" />Cultural meal planning</li>
                    <li className="flex items-center gap-2 text-zinc-300"><Check className="w-4 h-4 text-[#CCFF00]" />Weekly progression</li>
                    <li className="flex items-center gap-2 text-zinc-300"><Check className="w-4 h-4 text-[#CCFF00]" />Progress tracking</li>
                  </ul>
                  
                  <Button
                    onClick={() => navigate('/register')}
                    className={`w-full rounded-full font-bold ${
                      plan.popular
                        ? 'bg-[#CCFF00] text-black hover:bg-[#B3E600]'
                        : plan.bestValue
                        ? 'bg-emerald-500 text-white hover:bg-emerald-400'
                        : 'bg-zinc-800 text-white hover:bg-zinc-700'
                    }`}
                    data-testid={`select-${plan.id}-btn`}
                  >
                    Start Free Trial
                  </Button>
                </motion.div>
              ))}
            </div>
            
            {/* All plans include */}
            <div className="text-center">
              <p className="text-zinc-500 mb-4">All plans include:</p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="px-3 py-1 rounded-full bg-zinc-800/50 text-zinc-300">✓ Personalised training</span>
                <span className="px-3 py-1 rounded-full bg-zinc-800/50 text-zinc-300">✓ Cultural meal planning</span>
                <span className="px-3 py-1 rounded-full bg-zinc-800/50 text-zinc-300">✓ Allergy-aware nutrition</span>
                <span className="px-3 py-1 rounded-full bg-zinc-800/50 text-zinc-300">✓ Weekly progression</span>
                <span className="px-3 py-1 rounded-full bg-zinc-800/50 text-zinc-300">✓ Progress tracking</span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-24 bg-zinc-950">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-black mb-4">
                Frequently Asked <span className="text-[#CCFF00]">Questions</span>
              </h2>
            </motion.div>
            
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="rounded-xl border border-zinc-800 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                    className="w-full p-5 flex items-center justify-between bg-zinc-900/50 hover:bg-zinc-900 transition-colors text-left"
                    data-testid={`faq-${i}`}
                  >
                    <span className="font-medium pr-4">{faq.q}</span>
                    {openFAQ === i ? (
                      <ChevronUp className="w-5 h-5 text-[#CCFF00] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-zinc-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFAQ === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      className="px-5 pb-5 text-zinc-400"
                    >
                      {faq.a}
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-24 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-6">
                Ready to Transform <span className="text-[#CCFF00]">Your Body?</span>
              </h2>
              <p className="text-xl text-zinc-400 mb-8 max-w-2xl mx-auto">
                Join thousands of people who've already started their personalised fitness journey with Vyta-Fit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-10 py-6 text-lg"
                  data-testid="final-cta-btn"
                >
                  Start Your 7-Day Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 bg-zinc-950 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-[#CCFF00] flex items-center justify-center">
                    <span className="text-black font-black">V</span>
                  </div>
                  <span className="font-bold">VYTA</span>
                </div>
                <p className="text-sm text-zinc-500">Your digital personal coach + nutritionist, available globally.</p>
              </div>
              <div>
                <h4 className="font-bold mb-4">Product</h4>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-white transition">How It Works</button></li>
                  <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition">Pricing</button></li>
                  <li><Link to="/register" className="hover:text-white transition">Start Free Trial</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4">Company</h4>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li><Link to="/about" className="hover:text-white transition">About Us</Link></li>
                  <li><Link to="/science" className="hover:text-white transition">Our Science</Link></li>
                  <li><Link to="/challenge" className="hover:text-white transition">5-Week Challenge</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4">Legal</h4>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li><Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="hover:text-white transition">Terms of Service</Link></li>
                </ul>
              </div>
            </div>
            <div className="pt-8 border-t border-zinc-800 text-center text-sm text-zinc-500">
              © {new Date().getFullYear()} VYTA. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
